<template>
  <div>
    <a
      v-if="notifications.length"
      :title="$tc('unreadNotifications', notifications.length)"
      @click="_showNotification(notifications[0])"
    >
      <v-badge
        class="peer-unread-notifications"
        icon="far fa-bell-on"
        color="accent"
        overlap
      ></v-badge>
    </a>

    <label
      v-longpress="_onLongPress"
      class="peer d-inline-block"
      :peer-id="peer.id"
      :drop="drop"
      :connection="connection"
      :transfer-available="transferAvailable"
      :waiting="!!(peer.pending || peer.request)"
      @drop.prevent="_onDrop"
      @dragend="_onDragEnd"
      @dragleave="_onDragEnd"
      @dragover="_onDragOver"
      @click="_onClick"
      @click.right.prevent="_onRightClick"
    >
      <div class="device d-inline-block">
        <v-icon class="device-icon">{{icon}}</v-icon>

        <v-tooltip top>
          <template #activator="{on, attrs}">
            <v-icon
              class="candidate-type"
              small
              v-bind="attrs"
              v-on="on"
            >
              <template v-if="connection === 'relayed'">far fa-cloud-upload</template>
              <template v-else-if="connection === 'direct'">far fa-network-wired</template>
              <template v-else>far fa-unlink</template>
            </v-icon>
          </template>
          <template v-if="connection === 'relayed'">Über Server verbunden</template>
          <template v-else-if="connection === 'direct'">Direkt verbunden</template>
          <template v-else>Nicht verbunden</template>
        </v-tooltip>

        <div v-show="peer.pending || peer.request || peer.transfer" class="progress-background"></div>
        <div
          v-show="peer.transfer"
          class="progress"
          :class="{'progress-over50': peer.progress > 0.5}"
          :style="{'--progress': `rotate(${360 * peer.progress}deg)`}"
        >
          <div class="progress-circle"></div>
          <div class="progress-circle right"></div>
        </div>
      </div>
      <div class="name">
        <span :title="peer.name.userName">{{userName}}</span>
        <br>
        <small class="grey--text">{{device}}</small>
      </div>
    </label>
  </div>
</template>

<script>
  import eventBus from '../../assets/js/event-bus';
  import {getDeviceName, getDeviceIcon, getUserName} from './peer-utils';
  import {show as showNotification} from '../notifications/notification-utils';


  export default {
    name: 'Peer',

    props: {
      peer: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return {
        drop: false
      };
    },

    computed: {
      device() {
        return getDeviceName(this.peer);
      },

      icon() {
        const {peer} = this;
        const transferType = peer.transfer?.type;
        if (peer.pending || transferType === 'send') {
          return 'far fa-arrow-to-top';
        }
        if (peer.request || transferType === 'receive') {
          return 'far fa-arrow-from-top';
        }
        return getDeviceIcon(peer);
      },

      userName() {
        return getUserName(this.peer);
      },

      connection() {
        const {candidateTypes} = this.peer;
        const local = candidateTypes?.local;
        const remote = candidateTypes?.remote;
        if (local && remote) {
          return [local, remote].includes('relay') ? 'relayed' : 'direct';
        }
        return null;
      },

      notifications() {
        return this.$store.getters['share/getNotifications'].filter((n) => {
          return n.type.startsWith('peer-') && n.data?.peer?.id === this.peer.id;
        });
      },

      transferAvailable() {
        const {peer} = this;
        return this.connection && !peer.pending && !peer.request && !peer.transfer && peer.id !== 'example';
      }
    },

    methods: {
      _onDrop(e) {
        if (this.transferAvailable) {
          eventBus.$emit('peerSelectFiles', {
            files: [...e.dataTransfer.files],
            peer: this.peer
          });
        }
        this._onDragEnd();
      },

      _onDragOver() {
        this.drop = true;
      },

      _onDragEnd() {
        this.drop = false;
      },

      _onClick() {
        const {peer} = this;
        if (this.transferAvailable) {
          eventBus.$emit('peerSelectFiles', {peer});

        } else if (peer.pending) {
          const notification = this.notifications.find((n) => n.type === 'peer-files-pending');
          showNotification(notification);

        } else if (peer.request) {
          const notification = this.notifications.find((n) => n.type === 'peer-files-request');
          showNotification(notification);
        }
      },

      _onRightClick() {
        if (this.connection && this.peer.id !== 'example') {
          eventBus.$emit('textRecipient', this.peer);
        }
      },

      _onLongPress() {
        if (this.connection && this.peer.id !== 'example') {
          eventBus.$emit('textRecipient', this.peer);
        }
      },

      _showNotification(notification) {
        showNotification(notification);
      }
    }
  };
</script>
